import { EventEmitter, Injectable } from '@angular/core'
import { deserialize, serialize } from 'bson'
import { Subscription } from 'rxjs'
import { environment } from 'src/environments/environment'
import { SignalrCommunicationEngine } from 'src/lib/signalr-com/comm-engine'
import { CommMessageModel } from 'src/lib/signalr-com/comm-message-model'
import { Exam } from 'src/app/services/exam/interfaces/exam.interface'
import { PrepareExam } from './interfaces/prepare-exam.interface'
import { StopExam } from './interfaces/stop-exam.interface'
import { StartExamResponse } from './interfaces/start-exam-response.interface'
import { UserExam } from './interfaces/user-exam.interface'
import { UnlockCodeChangeNotification } from './interfaces/unlock-code-change-notification'
import { CommErrorModel } from 'src/lib/signalr-com/comm-error-model'
import { ToastrDialogService } from '../toastr-dialog.service'
import { ApiNamesEnum } from 'src/app/utils/enums'
import { IExamListModel } from 'src/app/components/examMode/interfaces'
import { DefaultHttpClient, HttpError, HttpRequest, HttpResponse } from '@microsoft/signalr'
import { AuthService } from '../auth/auth.service'

enum ComContentType {
  // Exams
  StartExam = 0,
  StopExam = 1,
  PrepareExam = 2,
  ReconnectToExam = 4,
  UnlockCodeChanged = 6,
  UpdateExam = 7,
  //Responses
  StartExamResponse = 10,
  // Statistics
  StatisticsData = 300,
  // Errors
  Error = 400,
}

@Injectable({
  providedIn: 'root'
})
export class ExamCommunicationService {
  private readonly subscriptions: Subscription = new Subscription();

  private communicationService: SignalrCommunicationEngine | undefined = undefined;

  public prepareExamFired: EventEmitter<PrepareExam> = new EventEmitter();
  public startExamFired: EventEmitter<Exam> = new EventEmitter();
  public stopExamFired: EventEmitter<StopExam> = new EventEmitter();
  public reconnectFired: EventEmitter<UserExam> = new EventEmitter();
  public unlockChangedFired: EventEmitter<string> = new EventEmitter();
  public updateExamFired: EventEmitter<IExamListModel> = new EventEmitter();

  constructor(
    private readonly toastrService: ToastrDialogService,
    private readonly authService: AuthService,
  ) { }

  public initialize(accessTokenFactory: () => Promise<string>): void {
    if (this.communicationService) {
      throw Error('Impossible to initialize working engine.')
    }

    this.communicationService = new SignalrCommunicationEngine(
      environment.signalrCom.exam.signalrHubUrl,
      environment.signalrCom.exam.msgSendMethod,
      accessTokenFactory,
      new CustomHttpClient(this.authService.getToken.bind(this.authService))
    )
    this.communicationService.onMsg(environment.signalrCom.exam.msgReceiveMethod, this.onDataReceived.bind(this))
    this.communicationService.connect()
  }

  public dispose(): void {
    this.subscriptions.unsubscribe()
    this.communicationService?.close()
    delete this.communicationService
  }

  private async onDataReceived(msg: CommMessageModel): Promise<void> {
    const contentType = msg.ContentType
    console.log('onDataReceived', msg)
    switch (contentType) {
      case ComContentType.PrepareExam: {
        const examData = deserialize(Uint8Array.from(msg.MessageData)) as PrepareExam
        this.prepareExamFired.emit(examData)
        break
      }
      case ComContentType.StartExam: {
        const examData = deserialize(Uint8Array.from(msg.MessageData)) as Exam
        this.startExamFired.emit(examData)
        break
      }
      case ComContentType.StopExam: {
        const exam = deserialize(Uint8Array.from(msg.MessageData)) as StopExam
        this.stopExamFired.emit(exam)
        break
      }
      case ComContentType.ReconnectToExam: {
        const userExam = deserialize(Uint8Array.from(msg.MessageData)) as UserExam
        this.reconnectFired.emit(userExam)
        break
      }
      case ComContentType.UnlockCodeChanged: {
        const examData = deserialize(Uint8Array.from(msg.MessageData)) as UnlockCodeChangeNotification
        this.unlockChangedFired.emit(examData.ExamName)
        break
      }
      case ComContentType.UpdateExam: {
        const examData = deserialize(Uint8Array.from(msg.MessageData)) as IExamListModel
        this.updateExamFired.emit(examData)
        break
      }
      case ComContentType.Error: {
        const error = deserialize(Uint8Array.from(msg.MessageData)) as CommErrorModel
        this.toastrService.showApiErrorToastr(error.errorCode, ApiNamesEnum.Woland, 500)
        break
      }
    }
  }

  public examStartedResponse(examId: number) {
    var response: StartExamResponse = {
      ExamId: examId,
    }

    const msg: CommMessageModel = {
      ContentType: ComContentType.StartExamResponse,
      MessageData: serialize(response),
      MessageId: '',
      ReceiverId: '',
      SenderId: '',
      Timings: {},
    }

    this.communicationService?.sendMsg(msg)
  }
}

class CustomHttpClient extends DefaultHttpClient {
  // private tokenRefreshCall: () => Promise<string>
  private getToken: () => Promise<string>

  constructor(getToken) {
    super(console) // the base class wants a signalR.ILogger
      this.getToken = getToken
  }
  public async send(
    request: HttpRequest
  ): Promise<HttpResponse> {
    const authHeaders = {
      Authorization: `Bearer ${await this.getToken()}`,
    }
    request.headers = { ...request.headers, ...authHeaders }

    try {
      const response = await super.send(request)
      return response
    } catch (er) {
      if (er instanceof HttpError) {
        const error = er as HttpError
        if (error.statusCode == 401) {
          const authHeaders = {
            Authorization: `Bearer ${await this.getToken()}`,
          }
          request.headers = { ...request.headers, ...authHeaders }
        }
      } else {
        throw er
      }
    }
    return super.send(request)
  }
}
